// Stage 5 C-A §18–§23 — Paid Services contextual overlays (562-01..06).
//
// These are CONTEXTUAL detail routes reached from Overview / Actions /
// Files.  There is NO permanent Paid Services / Commercial / Contracts
// / Payments nav entry (§3).  562-07 is explicitly NOT implemented (§24).
//
// Semantics enforced (§25):
//   Executed ≠ Effective; Approved ≠ Instructed; Certified ≠ Paid;
//   Package completion ≠ Practical Completion; Construction progress
//   ≠ Project State; Programme date ≠ Contractual date.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { portalApi, fmtDate, fmtMoney } from "./portalApi";
import {
    PageHeader, Card, CardHeader, Pill, Button, EmptyState, Spinner,
    Breadcrumbs,
} from "./PortalUi";
import ExecutedDocumentsTile from "./ExecutedDocumentsTile";
import { usePageMeta } from "../../components/Layout";

// ---------- 562-02 Package Detail ----------

export function PortalPackageDetail() {
    const { packageId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const pkgLabel = data?.package?.package_name || "Service package";
    usePageMeta({ title: `${pkgLabel} | Studio G4 Portal` });
    useEffect(() => {
        portalApi.package(packageId).then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, [packageId]);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Package unavailable" note={err} testid="portal-package-err" /></div>;
    if (!data) return <Spinner testid="portal-package-loading" />;
    const p = data.package;
    return (
        <div className="py-8 px-4" data-testid="portal-package-detail">
            <Breadcrumbs items={[{ label: "Overview", to: "/portal" }, { label: p.package_name }]} />
            <PageHeader kicker="Service package" title={p.package_name}
                right={<Pill tone={p.status === "Active" ? "ok" : "neutral"}>{p.status_label || p.status}</Pill>}
                testid="portal-package-header" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Contract basis</div>
                    <div className="text-[14px] text-charcoal mt-1 font-manrope">{p.contract_basis_label || "—"}</div>
                </div></Card>
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Fee</div>
                    <div className="text-[14px] text-charcoal mt-1 font-manrope">{fmtMoney(p.fee_amount, p.fee_currency)}</div>
                    <div className="text-[11.5px] text-[#8A8A84] mt-1">{p.pricing_method_label || "—"}</div>
                </div></Card>
                <Card><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Activated</div>
                    <div className="text-[14px] text-charcoal mt-1 font-manrope">{fmtDate(p.activated_at)}</div>
                </div></Card>
            </div>

            {p.scope_summary && (
                <Card className="mb-4"><div className="p-4">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold mb-1">Scope</div>
                    <div className="text-[13.5px] text-[#4A4A46] leading-relaxed">{p.scope_summary}</div>
                </div></Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card testid="portal-package-programme">
                    <CardHeader title="Linked programme" subtle right={<Link to="/portal/programme" className="text-[11.5px] text-[#A8543F] font-manrope font-semibold">All →</Link>} />
                    <div className="p-4">
                        {(data.programme || []).length === 0 ? (
                            <div className="text-[13px] text-[#6B6B67]">No linked programme items.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEEBE4]">
                                {data.programme.map(it => (
                                    <li key={it.id} className="py-2 flex items-center justify-between" data-testid={`portal-package-prog-${it.id}`}>
                                        <span className="text-[13px] text-charcoal">{it.title}</span>
                                        <span className="text-[11.5px] text-[#8A8A84]">{fmtDate(it.target_at)}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Card>
                <Card testid="portal-package-files">
                    <CardHeader title="Related files" subtle right={<Link to="/portal/files" className="text-[11.5px] text-[#A8543F] font-manrope font-semibold">All →</Link>} />
                    <div className="p-4">
                        {(data.files || []).length === 0 ? (
                            <div className="text-[13px] text-[#6B6B67]">No linked files.</div>
                        ) : (
                            <ul className="divide-y divide-[#EEEBE4]">
                                {data.files.map(f => (
                                    <li key={f.id} className="py-2 flex items-center justify-between" data-testid={`portal-package-file-${f.id}`}>
                                        <Link to={`/portal/files/${f.id}/preview`} className="text-[13px] text-charcoal hover:text-[#A8543F]">{f.filename || f.id}</Link>
                                        <span className="text-[11.5px] text-[#8A8A84]">{f.category_label || f.category}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </Card>
            </div>

            {p.contract_record_id && (
                <div className="mt-4">
                    <Link to={`/portal/paid-services/contracts/${p.contract_record_id}`}>
                        <Button variant="secondary" testid="portal-package-view-contract">View contract position →</Button>
                    </Link>
                </div>
            )}
        </div>
    );
}

// ---------- 562-03 Contract Position ----------

export function PortalContractPosition() {
    const { contractId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const contractLabel = data?.contract?.contract_title || "Contract";
    usePageMeta({ title: `${contractLabel} · Contract | Studio G4 Portal` });
    useEffect(() => {
        portalApi.contract(contractId).then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, [contractId]);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Contract unavailable" note={err} testid="portal-contract-err" /></div>;
    if (!data) return <Spinner testid="portal-contract-loading" />;
    const c = data.contract;
    const pos = data.commercial_position || {};
    const changes = data.contract_changes || [];
    const certs = data.certificates || [];
    return (
        <div className="py-8 px-4" data-testid="portal-contract-position">
            <Breadcrumbs items={[{ label: "Overview", to: "/portal" }, { label: "Contract position" }]} />
            <PageHeader kicker="Contract" title={c.contract_title}
                right={<Pill tone={c.effective_state === "effective" ? "ok" : "info"}>{c.effective_state_label || c.effective_state}</Pill>}
                testid="portal-contract-header" />

            {/* Executed ≠ Effective — show both distinctly (§25) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card testid="portal-contract-executed"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Executed</div>
                    <div className="text-[13px] text-charcoal mt-1">{fmtDate(c.executed_at)}</div>
                </div></Card>
                <Card testid="portal-contract-effective"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Effective</div>
                    <div className="text-[13px] text-charcoal mt-1">{fmtDate(c.effective_at)}</div>
                </div></Card>
                <Card testid="portal-contract-commencement"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Commencement</div>
                    <div className="text-[13px] text-charcoal mt-1">{fmtDate(c.commencement_date)}</div>
                </div></Card>
                <Card testid="portal-contract-completion"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Current contract completion</div>
                    <div className="text-[13px] text-charcoal mt-1">{fmtDate(c.completion_date)}</div>
                </div></Card>
            </div>

            <Card className="mb-4" testid="portal-contract-commercial">
                <CardHeader title="Commercial position" subtle />
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
                    <PositionRow label="Original contract sum" value={fmtMoney(pos.original_contract_sum)} testid="portal-contract-original-sum" />
                    <PositionRow label="Approved changes" value={fmtMoney(pos.approved_changes_value)} testid="portal-contract-approved-value" />
                    <PositionRow label="Pending changes" value={fmtMoney(pos.pending_changes_value)} testid="portal-contract-pending-value" />
                    <PositionRow label="Current adjusted contract sum" value={fmtMoney(pos.current_adjusted_contract_sum)} strong testid="portal-contract-adjusted-sum" />
                    <PositionRow label="Certified to date" value={fmtMoney(pos.certified_to_date)} testid="portal-contract-certified" />
                    {/* Paid to Date ONLY shown when reliably confirmed (§20/§25) */}
                    {pos.paid_to_date != null && (
                        <PositionRow label="Paid to date" value={fmtMoney(pos.paid_to_date)} testid="portal-contract-paid" />
                    )}
                </div>
            </Card>

            {changes.length > 0 && (
                <Card className="mb-4" testid="portal-contract-changes">
                    <CardHeader title="Contract changes" subtle />
                    <div className="divide-y divide-[#EEEBE4]">
                        {changes.map(x => (
                            <Link key={x.id} to={`/portal/paid-services/contract-changes/${x.id}`}
                                  className="block px-5 py-3 hover:bg-[#FAF7F1]" data-testid={`portal-contract-change-${x.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="font-manrope text-[13.5px] text-charcoal">{x.reference} · {x.description}</div>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5">
                                            Approval: {x.client_approval_label || "—"} · Instruction: {x.instruction_label || "—"}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[13px] text-charcoal">{fmtMoney(x.cost_effect)}</div>
                                        {x.time_effect_days ? <div className="text-[11.5px] text-[#8A8A84]">+{x.time_effect_days}d proposed</div> : null}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {certs.length > 0 && (
                <Card testid="portal-contract-certificates">
                    <CardHeader title="Certificates" subtle />
                    <div className="divide-y divide-[#EEEBE4]">
                        {certs.map(pc => (
                            <Link key={pc.id} to={`/portal/paid-services/certificates/${pc.id}`}
                                  className="block px-5 py-3 hover:bg-[#FAF7F1]" data-testid={`portal-contract-cert-${pc.id}`}>
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="font-manrope text-[13.5px] text-charcoal">{pc.certificate_reference}</div>
                                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5">Issued {fmtDate(pc.issued_at)}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-[13px] text-charcoal">{fmtMoney(pc.certified_amount)}</div>
                                        <div className="text-[11.5px]"><Pill tone={pc.paid_at ? "ok" : "info"}>{pc.paid_at ? "Paid" : (pc.status_label || pc.status)}</Pill></div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {/* Phase 6 — Executed documents linked to THIS contract
                (contract itself + any client-visible executed changes
                 under it). */}
            <div className="mt-4" data-testid="portal-contract-executed-docs-wrap">
                <ExecutedDocumentsTile
                    filter={d =>
                        (d.instrument_kind === "contract_record"
                         && d.instrument_id === c.id)
                        || (d.instrument_kind === "contract_change"
                            && (changes || []).some(x => x.id === d.instrument_id))} />
            </div>
        </div>
    );
}

function PositionRow({ label, value, strong, testid }) {
    return (
        <div className="flex items-center justify-between border-b border-[#EEEBE4] pb-2" data-testid={testid}>
            <span className="text-[12.5px] text-[#6B6B67]">{label}</span>
            <span className={`font-manrope ${strong ? "text-[15px] font-semibold text-charcoal" : "text-[13px] text-charcoal"}`}>{value}</span>
        </div>
    );
}

// ---------- 562-04 Contract Change ----------

export function PortalContractChange() {
    const { changeId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const changeLabel = data?.change?.reference || "Contract change";
    usePageMeta({ title: `${changeLabel} · Change | Studio G4 Portal` });
    useEffect(() => {
        portalApi.contractChange(changeId).then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, [changeId]);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Change unavailable" note={err} testid="portal-change-err" /></div>;
    if (!data) return <Spinner testid="portal-change-loading" />;
    const x = data.change;
    return (
        <div className="py-8 px-4" data-testid="portal-contract-change">
            <Breadcrumbs items={[{ label: "Overview", to: "/portal" }, { label: `Change ${x.reference}` }]} />
            <PageHeader kicker="Contract change" title={x.reference}
                right={<Pill tone={x.status === "instructed" ? "ok" : "info"}>{x.change_type_label || x.status}</Pill>}
                testid="portal-change-header" />

            <Card className="mb-4"><div className="p-5 text-[13.5px] text-[#4A4A46] leading-relaxed">{x.description}</div></Card>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <Card testid="portal-change-cost"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Cost effect</div>
                    <div className="text-[15px] font-manrope font-semibold text-charcoal mt-1">{fmtMoney(x.cost_effect)}</div>
                </div></Card>
                <Card testid="portal-change-time"><div className="p-3">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Time effect (proposed)</div>
                    <div className="text-[15px] font-manrope font-semibold text-charcoal mt-1">{x.time_effect_days ? `${x.time_effect_days} days` : "—"}</div>
                    {x.time_effect_days ? <div className="text-[10.5px] text-[#6B6B67] mt-1 italic">Proposed programme effect does not automatically extend contract completion.</div> : null}
                </div></Card>
            </div>

            {/* §21 — Client Approval / Formal Instruction / Contractual Effect are distinct */}
            <Card className="mb-4" testid="portal-change-authority-separation">
                <CardHeader title="Authority position" subtle />
                <div className="p-4 grid grid-cols-3 gap-3 text-[12px]">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Client approval</div>
                        <div className="mt-1"><Pill tone={x.client_approval_state === "approved" ? "ok" : "neutral"}>{x.client_approval_label || "—"}</Pill></div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Formal instruction</div>
                        <div className="mt-1"><Pill tone={x.instruction_state === "instructed" ? "ok" : "neutral"}>{x.instruction_label || "—"}</Pill></div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-[#9A9A94] font-semibold">Contractual effect</div>
                        <div className="mt-1"><Pill tone={x.instruction_state === "instructed" ? "ok" : "neutral"}>{x.instruction_state === "instructed" ? "In force" : "Pending instruction"}</Pill></div>
                    </div>
                </div>
            </Card>
            {(data.files || []).length > 0 && (
                <Card testid="portal-change-files">
                    <CardHeader title="Supporting files" subtle />
                    <div className="divide-y divide-[#EEEBE4]">
                        {data.files.map(f => (
                            <Link key={f.id} to={`/portal/files/${f.id}/preview`} className="block px-5 py-2.5 hover:bg-[#FAF7F1]" data-testid={`portal-change-file-${f.id}`}>
                                <div className="text-[13px] text-charcoal">{f.filename}</div>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}

            {/* Phase 6 — Executed document(s) for THIS change, if any. */}
            <div className="mt-4" data-testid="portal-change-executed-docs-wrap">
                <ExecutedDocumentsTile
                    filter={d =>
                        d.instrument_kind === "contract_change"
                        && d.instrument_id === x.id} />
            </div>
        </div>
    );
}

// ---------- 562-05 Payment / Certification ----------

export function PortalCertificateDetail() {
    const { certId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const certLabel = data?.certificate?.certificate_reference || "Certificate";
    usePageMeta({ title: `${certLabel} · Payment | Studio G4 Portal` });
    useEffect(() => {
        portalApi.certificate(certId).then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, [certId]);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Certificate unavailable" note={err} testid="portal-cert-err" /></div>;
    if (!data) return <Spinner testid="portal-cert-loading" />;
    const c = data.certificate;
    return (
        <div className="py-8 px-4" data-testid="portal-certificate-detail">
            <Breadcrumbs items={[
                { label: "Overview", to: "/portal" },
                { label: "Contract position", to: c.contract_record_id ? `/portal/paid-services/contracts/${c.contract_record_id}` : "/portal" },
                { label: c.certificate_reference || "Certificate" },
            ]} />
            <PageHeader kicker="Payment certificate" title={c.certificate_reference}
                right={<Pill tone={c.paid_at ? "ok" : "info"} testid="portal-cert-status-pill">{c.paid_at ? "Paid" : (c.status_label || c.status)}</Pill>}
                testid="portal-cert-header" />

            <Card className="mb-4"><div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
                <PositionRow label="Certified amount" value={fmtMoney(c.certified_amount)} strong testid="portal-cert-amount" />
                <PositionRow label="Cumulative certified to date" value={fmtMoney(c.cumulative_certified_to_date)} testid="portal-cert-cumulative" />
                <PositionRow label="Issued" value={fmtDate(c.issued_at)} testid="portal-cert-issued" />
                <PositionRow label="Recipient" value={c.recipient_name || "—"} testid="portal-cert-recipient" />
                {c.retention_amount != null && (
                    <PositionRow label={`Retention${c.retention_percentage ? ` (${c.retention_percentage}%)` : ""}`} value={fmtMoney(c.retention_amount)} testid="portal-cert-retention" />
                )}
                {c.paid_at && (
                    <PositionRow label="Paid" value={fmtDate(c.paid_at)} testid="portal-cert-paid" />
                )}
            </div></Card>

            {/* §22 — Certificate issue ≠ confirmed payment. */}
            {!c.paid_at && (
                <div className="text-[11.5px] italic text-[#6B6B67] px-4"
                     data-testid="portal-cert-unpaid-note">
                    Certificate issue does not itself constitute payment. Payment confirmation will appear here once received.
                </div>
            )}
        </div>
    );
}

// ---------- 562-06 Construction Progress / Handover ----------

export function PortalConstructionProgress() {
    usePageMeta({ title: "Construction progress | Studio G4 Portal" });
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.constructionProgress().then(r => setData(r.data))
            .catch(e => setErr(e?.response?.data?.detail || "Unavailable"));
    }, []);
    if (err) return <div className="py-8 px-4"><EmptyState heading="Construction progress unavailable" note={err} testid="portal-construction-err" /></div>;
    if (!data) return <Spinner testid="portal-construction-loading" />;
    const milestones = data.milestones || [];
    const reqs = data.ws7_requirements || [];
    const files = data.progress_files || [];
    return (
        <div className="py-8 px-4" data-testid="portal-construction-progress">
            <Breadcrumbs items={[{ label: "Overview", to: "/portal" }, { label: "Construction progress" }]} />
            <PageHeader kicker="Delivery" title="Construction progress"
                testid="portal-construction-header" />

            {milestones.length === 0 && reqs.length === 0 && files.length === 0 ? (
                <EmptyState heading="Not yet in construction"
                    note="Construction progress and handover information will appear here once your project moves into delivery."
                    testid="portal-construction-empty" />
            ) : (
                <>
                    {milestones.length > 0 && (
                        <Card className="mb-4" testid="portal-construction-milestones">
                            <CardHeader title="Milestones" subtle />
                            <div className="divide-y divide-[#EEEBE4]">
                                {milestones.map(m => (
                                    <div key={m.id} className="px-5 py-3 flex items-center justify-between" data-testid={`portal-construction-ms-${m.id}`}>
                                        <div>
                                            <div className="font-manrope text-[13.5px] text-charcoal">{m.title}</div>
                                            <div className="text-[11.5px] text-[#8A8A84] mt-0.5">{m.linked_workstage ? `WS${m.linked_workstage}` : ""} · {m.kind}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[12.5px] text-charcoal">{fmtDate(m.target_at)}</div>
                                            <Pill tone={m.state === "achieved" ? "ok" : "info"}>{m.state}</Pill>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                    {reqs.length > 0 && (
                        <Card className="mb-4" testid="portal-construction-handover">
                            <CardHeader title="Handover requirements" subtle />
                            <div className="divide-y divide-[#EEEBE4]">
                                {reqs.map(r => (
                                    <div key={r.id} className="px-5 py-3 flex items-center justify-between" data-testid={`portal-construction-req-${r.id}`}>
                                        <div className="min-w-0 text-[13.5px] text-charcoal font-manrope">{r.description}</div>
                                        {r.blocking && <Pill tone="warn">Blocking</Pill>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}
                    {files.length > 0 && (
                        <Card testid="portal-construction-files">
                            <CardHeader title="Progress documents" subtle />
                            <div className="divide-y divide-[#EEEBE4]">
                                {files.map(f => (
                                    <Link key={f.id} to={`/portal/files/${f.id}/preview`} className="block px-5 py-2.5 hover:bg-[#FAF7F1]" data-testid={`portal-construction-file-${f.id}`}>
                                        <div className="text-[13px] text-charcoal">{f.filename}</div>
                                        <div className="text-[11.5px] text-[#8A8A84]">{f.category_label || f.category} · {fmtDate(f.created_at)}</div>
                                    </Link>
                                ))}
                            </div>
                        </Card>
                    )}
                </>
            )}

            <div className="mt-6 p-4 bg-[#FAF7F1] rounded text-[11.5px] italic text-[#6B6B67] max-w-3xl"
                 data-testid="portal-construction-authoritative-note">
                Construction progress is distinct from Programme progress, Project State and Practical Completion. Refer to the contract record for authoritative contractual milestones.
            </div>
        </div>
    );
}
